import {
    CORRECTION_POINTS_FOREIGN,
    ERROR_REPORT_POINTS,
    friendPurchasePoints,
    MINI_TEST_POINTS,
    POINTS_V4 as P,
    purchasePoints,
    REFERRAL_BALANCE_PERCENT,
    REFERRAL_DIAGNOSTIC_POINTS,
    REVIEW_POINTS,
    SPHERE_SCALE_V82,
    SUP_QUESTION_POINTS,
    WEEK_ON_PLAN_POINTS,
} from "./rules";
import { isArabicUi } from "@/shared/config/arabicUi";
import { productById, SUP_PLAN_PRODUCT } from "@/shared/config/products";
import { arPlural } from "@/shared/lib/plural/arabicPlural";
import { plural } from "@/features/personProfile/lib/format";
import type { PointsActionKey } from "./pointsActions";
import { formatGain, SphereKey } from "./spheres";

/**
 * Экран «Как получить баллы» (просьба владельца 29.09.2026): вместо формул
 * оценки тренировки — пять сфер и за что каждая даёт баллы. Просто и
 * по-игровому: без денег (ни цен, ни «за каждый $1»), без пределов, порогов,
 * округлений и того, как это устроено внутри, — покупка показана готовым числом
 * баллов. «Подробнее» — 1–3 коротких строки: только то, что меняет, что человек
 * получит («вернули покупку — баллы снимаются»), и маленькие таблицы «что →
 * сколько». Особые случаи — вопрос в поддержку (владелец 29.09: «это не
 * юридический документ, а развлекательный»).
 *
 * Все числа — из shared/config/pointsRules.ts (таблица v8.2), примеры покупок —
 * той же функцией, что и чипы, по ценам каталога (shared/config/products.ts):
 * поменялась таблица или цена — поменялся и этот экран.
 *
 * Тестовая тренировка — во «Вкладе», а не в «Тренировках» (владелец 01.10,
 * ДОПОЛНЕНИЕ §7: «все тестовые тренировки … засчитывать как помощь проекту»):
 * сумма и предел те же, другая только сфера; плана для неё не нужно.
 */

/** Цена продукта каталога без скидки — только чтобы посчитать баллы примера. */
const listPrice = (id: string): number => productById(id)?.listPriceCents ?? 0;

/** Примеры «покупка → баллы» (экран денег не показывает). */
export const HOW_TO_EXAMPLES = {
    coursePart: purchasePoints(listPrice("course:effective-health")),
    appYear: purchasePoints(listPrice("app:my-revive-year")),
    bundle: purchasePoints(listPrice("package:pack-2-system")),
    diagnostic: purchasePoints(listPrice("diag:zog"), 0, "diagnostic"),
    /** Самый маленький пакет токенов (10 токенов) — строка «Вклад → А ещё». */
    tokenPack: purchasePoints(listPrice("tokens:sup")),
    /** Пакет «поиск + вопросы» на месяц — та же строка у человека на пакетах (.com). */
    planPack: purchasePoints(SUP_PLAN_PRODUCT.listPriceCents ?? 0),
    friendCoursePart: friendPurchasePoints(listPrice("course:effective-health")),
    friendAppYear: friendPurchasePoints(listPrice("app:my-revive-year")),
    friendBundle: friendPurchasePoints(listPrice("package:pack-2-system")),
    friendDiagnostic: REFERRAL_DIAGNOSTIC_POINTS,
};

/** «+18 500» приглашённых за часть курса друга — пример экрана «Как получить баллы». */
export const FRIEND_COURSE_PART_POINTS = HOW_TO_EXAMPLES.friendCoursePart;

/** При каком счёте шар сферы полон (таблица v8.2). */
export const HOW_TO_SCALE: Record<SphereKey, number> = SPHERE_SCALE_V82;

/**
 * Шкала «Тренировок» у уровня и у строки профиля — недели по плану, максимум 52 (порог уровня 6
 * лестницы workouts в каталоге; воркер profileLevelsDef, приложение WORKOUT_WEEKS_MAX). Карточка
 * «Правил» и окно «за что эти баллы» говорят то же, что профиль: «1 неделя по плану» и полоска
 * 1/52, а баллы — простым числом, без «из 100 000» (владелец 10.10: на профиле «1 неделя по
 * плану · 2 %», а в окне — «16 250 из 100 000»). 100 000 остаётся размером шара календаря.
 */
export const WORKOUT_WEEKS_FULL = 52;

/** Действие строки для окна «за что эти баллы» (model/pointsActions.ts). */
export type HowToRowAction = { action: PointsActionKey; ctx?: string };

/**
 * Каждая строка экрана нажимается и открывает окно «за что эти баллы» со своим действием
 * (владелец 03.10: «на каждую строчку … должна быть возможность нажать и получить нужные
 * ссылки … и краткое пояснение что нужно делать»): строки карточки (lines) и строки
 * маленьких таблиц «Подробнее» (more: у пункта-таблицы — по строке, у пункта-фразы — null).
 * Порядок — тот же, что у строк на каждом языке ниже (тест сверяет длины и подписи).
 */
export const HOW_TO_ROW_ACTIONS: Record<SphereKey, { lines: HowToRowAction[]; more: (HowToRowAction[] | null)[] }> = {
    contrib: {
        lines: [
            { action: "contrib.purchase", ctx: "anyPart" },
            { action: "contrib.purchase", ctx: "app" },
            { action: "contrib.purchase", ctx: "bundle" },
            { action: "contrib.review" },
            { action: "contrib.correction" },
            { action: "contrib.errorReport" },
            { action: "contrib.testWorkout" },
        ],
        more: [null, null, [{ action: "contrib.purchase", ctx: "tokens" }, { action: "contrib.purchase", ctx: "diagnostic" }]],
    },
    workouts: {
        lines: [{ action: "work.weekOnPlan" }, { action: "work.firstPlan" }, { action: "work.newbie" }],
        more: [null, null],
    },
    social: {
        lines: [
            { action: "social.meeting", ctx: "seva" },
            { action: "social.meeting", ctx: "club" },
            { action: "social.organizer" },
            { action: "social.joinClub" },
            { action: "social.goodDeed" },
        ],
        more: [
            (["seva", "health", "spirit", "edu", "club", "online"] as const).map((ctx) => ({ action: "social.meeting" as const, ctx })),
            [{ action: "social.addFriend" }, { action: "social.firstMessage" }],
        ],
    },
    referrals: {
        lines: [
            { action: "ref.install" },
            { action: "ref.started" },
            { action: "ref.newAtMeeting" },
            { action: "ref.purchase", ctx: "generic" },
        ],
        more: [
            null,
            [
                { action: "ref.purchase", ctx: "generic" },
                { action: "ref.purchase", ctx: "app" },
                { action: "ref.purchase", ctx: "bundle" },
            ],
            null,
        ],
    },
    learning: {
        lines: [
            { action: "learn.lessonMinute" },
            { action: "learn.miniTest" },
            { action: "learn.lessonQuestion" },
            { action: "learn.sup" },
            { action: "learn.minute" },
        ],
        more: [[{ action: "learn.appLearning" }, { action: "learn.specialist" }]],
    },
};

export type HowToLine = {
    label: string;
    /** «+15 000», «до +2 000». */
    amount: string;
    /** Мелко под названием: «за минуту», «один раз». */
    note?: string;
};

/** Строка «Подробнее» — фраза или маленькая таблица «что → сколько» (виды встреч). */
export type HowToMoreItem = string | { title: string; rows: HowToLine[] };

export type HowToSphere = {
    name: string;
    tagline: string;
    lines: HowToLine[];
    /** «Подробнее»: 1–3 коротких строки о важном; список — маленькой таблицей. */
    more: HowToMoreItem[];
    /** Куда пойти, чтобы заработать: подпись кнопки внизу «Подробнее». */
    go: string;
};

export type HowToTexts = {
    title: string;
    intro: string;
    /** «из 300 000» — рядом с баллами человека. */
    of: (scale: string) => string;
    /** Баллов человека не знаем — «Полный шар — 300 000». */
    fullAt: (scale: string) => string;
    /**
     * Шкала «Тренировок» — недели по плану (как строка профиля «1 неделя по плану»): слово после
     * числа. Те же слова, что у профиля v2 (personProfile texts unitWeeks).
     */
    unitWeeks: (n: number) => string;
    /** «16 250 баллов» — баллы без «из»: у «Тренировок» 100 000 — размер шара, а не максимум уровня. */
    unitPoints: (n: number) => string;
    more: string;
    moreTitle: (name: string) => string;
    close: string;
    spheres: Record<SphereKey, HowToSphere>;
};

// Числа — с неразрывными пробелами: «+3 000» в тексте «Подробнее» не рвётся на
// «+3» в конце строки и «000» в начале следующей.
const nbsp = (s: string): string => s.replace(/ /g, " ");
const g = (x: number): string => nbsp(formatGain(x));

const RU: HowToTexts = {
    title: "Как получить баллы",
    intro: "Всё, что вы делаете, наполняет один из пяти шаров. Вот за что и сколько.",
    of: (s) => `из ${s}`,
    fullAt: (s) => `Полный шар — ${s}`,
    unitWeeks: (n) => plural(n, "ru", { one: "неделя по плану", few: "недели по плану", many: "недель по плану", other: "недели по плану" }),
    unitPoints: (n) => plural(n, "ru", { one: "балл", few: "балла", many: "баллов", other: "балла" }),
    more: "Подробнее",
    moreTitle: (name) => `${name} — подробнее`,
    close: "Понятно",
    spheres: {
        contrib: {
            name: "Вклад",
            tagline: "Покупки, отзывы и помощь проекту",
            lines: [
                { label: "Часть курса", amount: g(HOW_TO_EXAMPLES.coursePart) },
                { label: "Год приложения MY REVIVE", amount: g(HOW_TO_EXAMPLES.appYear) },
                { label: "Пакет «Системный подход»", amount: g(HOW_TO_EXAMPLES.bundle) },
                { label: "Отзыв о продукте", amount: g(REVIEW_POINTS) },
                { label: "Правка субтитров лекции", amount: g(CORRECTION_POINTS_FOREIGN) },
                { label: "Ошибка на сайте или в рецепте", amount: g(ERROR_REPORT_POINTS) },
                { label: "Тестовая тренировка", amount: g(P.workouts.testWorkout) },
            ],
            more: [
                "Подарили курс — баллы ваши, как за свою покупку.",
                "Вернули покупку — её баллы снимаются.",
                {
                    title: "А ещё:",
                    rows: [
                        { label: "10 токенов для умного поиска", amount: g(HOW_TO_EXAMPLES.tokenPack) },
                        { label: "Диагностика", amount: g(HOW_TO_EXAMPLES.diagnostic) },
                    ],
                },
            ],
            go: "Все продукты",
        },
        workouts: {
            name: "Тренировки",
            tagline: "Тренировки по плану в приложении MY REVIVE",
            lines: [
                { label: "Неделя точно по плану", amount: g(WEEK_ON_PLAN_POINTS) },
                { label: "Первый план", amount: g(P.workouts.planCreate), note: "один раз" },
                { label: "Новичку на полном плане", amount: g(P.workouts.newbieBonus), note: "ещё за каждую полную неделю" },
            ],
            more: [
                "Баллы дают тренировки по плану: чем ближе неделя к плану, тем их больше.",
                "Составили план заново — баллов не прибавится.",
            ],
            go: "К тренировкам",
        },
        social: {
            name: "Общественное",
            tagline: "Встречи, клуб и добрые дела",
            lines: [
                { label: "Встреча-служение", amount: g(P.meeting.seva) },
                { label: "Встреча клуба", amount: g(P.meeting.club) },
                { label: "Организатору встречи", amount: g(P.meeting.perParticipant), note: "за каждого участника" },
                { label: "Вступить в клуб", amount: g(P.club.joinClub), note: "один раз" },
                { label: "Доброе дело", amount: g(P.club.goodDeed) },
            ],
            more: [
                {
                    title: "Пришли на встречу:",
                    rows: [
                        { label: "Служение", amount: g(P.meeting.seva) },
                        { label: "Оздоровление, спорт", amount: g(P.meeting.health) },
                        { label: "Духовная", amount: g(P.meeting.spirit) },
                        { label: "Обучение, культура", amount: g(P.meeting.edu) },
                        { label: "Клуб, общение", amount: g(P.meeting.club) },
                        { label: "Онлайн", amount: g(P.meeting.online) },
                    ],
                },
                {
                    title: "А ещё:",
                    rows: [
                        { label: "Добавить в друзья", amount: g(P.club.addFriend) },
                        { label: "Первое сообщение в чате", amount: g(P.club.firstMessage) },
                    ],
                },
            ],
            go: "Встречи и клуб",
        },
        referrals: {
            name: "Приглашённые",
            tagline: "Друзья, которые пришли с вами",
            lines: [
                { label: "Друг установил приложение", amount: g(P.referral.install) },
                { label: "Друг начал заниматься", amount: g(P.referral.friendStarted) },
                { label: "Новый человек на встрече", amount: g(P.referral.firstMeeting) },
                { label: "Друг купил по вашей ссылке", amount: g(HOW_TO_EXAMPLES.friendCoursePart), note: "за часть курса" },
            ],
            more: [
                `Вам — ${REFERRAL_BALANCE_PERCENT} % с покупок друга на баланс: их можно потратить на новые продукты.`,
                {
                    title: "Друг купил по вашей ссылке:",
                    rows: [
                        { label: "Часть курса", amount: g(HOW_TO_EXAMPLES.friendCoursePart) },
                        { label: "Год приложения MY REVIVE", amount: g(HOW_TO_EXAMPLES.friendAppYear) },
                        { label: "Пакет «Системный подход»", amount: g(HOW_TO_EXAMPLES.friendBundle) },
                    ],
                },
                "Друг забрал ваш подарок — баллы как за его покупку; вернул покупку — баллы снимаются.",
            ],
            go: "Пригласить друга",
        },
        learning: {
            name: "Обучение",
            tagline: "Курсы, лекции и умный поиск",
            lines: [
                { label: "Урок курса", amount: g(P.learning.lessonMinute), note: "за минуту" },
                { label: "Мини-тест по главе", amount: g(MINI_TEST_POINTS) },
                { label: "Вопрос по уроку", amount: g(P.learning.lessonQuestion) },
                { label: "Вопрос умному поиску", amount: g(SUP_QUESTION_POINTS) },
                { label: "Лекция или клип", amount: g(P.learning.freeMinute), note: "за минуту" },
            ],
            more: [
                {
                    title: "А ещё:",
                    rows: [
                        { label: "Гид или самодиагностика в приложении", amount: g(P.learning.appLearning) },
                        { label: "Обращение к специалисту", amount: g(P.learning.specialistTicket) },
                    ],
                },
            ],
            go: "Мои курсы",
        },
    },
};

const EN: HowToTexts = {
    title: "How to earn points",
    intro: "Everything you do fills one of five spheres. Here is what earns what.",
    of: (s) => `of ${s}`,
    fullAt: (s) => `Full sphere — ${s}`,
    unitWeeks: (n) => plural(n, "en", { one: "week on the plan", other: "weeks on the plan" }),
    unitPoints: (n) => plural(n, "en", { one: "point", other: "points" }),
    more: "More",
    moreTitle: (name) => `${name}: more`,
    close: "Got it",
    spheres: {
        contrib: {
            name: "Contribution",
            tagline: "Purchases, reviews and helping the project",
            lines: [
                { label: "Course part", amount: g(HOW_TO_EXAMPLES.coursePart) },
                { label: "MY REVIVE app, one year", amount: g(HOW_TO_EXAMPLES.appYear) },
                { label: "“Systematic Approach” package", amount: g(HOW_TO_EXAMPLES.bundle) },
                { label: "Product review", amount: g(REVIEW_POINTS) },
                { label: "Lecture subtitle fix", amount: g(CORRECTION_POINTS_FOREIGN) },
                { label: "A mistake on the site or in a recipe", amount: g(ERROR_REPORT_POINTS) },
                { label: "Test workout", amount: g(P.workouts.testWorkout) },
            ],
            more: [
                "Gave a course as a gift — the points are yours, as for your own purchase.",
                "A refund takes the purchase's points back.",
                {
                    title: "Also:",
                    rows: [
                        { label: "10 tokens for the smart search", amount: g(HOW_TO_EXAMPLES.tokenPack) },
                        { label: "A diagnostic", amount: g(HOW_TO_EXAMPLES.diagnostic) },
                    ],
                },
            ],
            go: "All products",
        },
        workouts: {
            name: "Practice",
            tagline: "Workouts on your plan in the MY REVIVE app",
            lines: [
                { label: "A week exactly on plan", amount: g(WEEK_ON_PLAN_POINTS) },
                { label: "Your first plan", amount: g(P.workouts.planCreate), note: "once" },
                { label: "Newbie on the full plan", amount: g(P.workouts.newbieBonus), note: "extra for every full week" },
            ],
            more: [
                "Points come from workouts on your plan: the closer the week is to the plan, the more points.",
                "Making a new plan adds no points.",
            ],
            go: "To workouts",
        },
        social: {
            name: "Community",
            tagline: "Meetings, the club and good deeds",
            lines: [
                { label: "Service meeting", amount: g(P.meeting.seva) },
                { label: "Club meeting", amount: g(P.meeting.club) },
                { label: "Meeting organizer", amount: g(P.meeting.perParticipant), note: "for every participant" },
                { label: "Join a club", amount: g(P.club.joinClub), note: "once" },
                { label: "Good deed", amount: g(P.club.goodDeed) },
            ],
            more: [
                {
                    title: "Came to a meeting:",
                    rows: [
                        { label: "Service", amount: g(P.meeting.seva) },
                        { label: "Health, sport", amount: g(P.meeting.health) },
                        { label: "Spiritual", amount: g(P.meeting.spirit) },
                        { label: "Learning, culture", amount: g(P.meeting.edu) },
                        { label: "Club, talk", amount: g(P.meeting.club) },
                        { label: "Online", amount: g(P.meeting.online) },
                    ],
                },
                {
                    title: "Also:",
                    rows: [
                        { label: "Add a friend", amount: g(P.club.addFriend) },
                        { label: "First message in a chat", amount: g(P.club.firstMessage) },
                    ],
                },
            ],
            go: "Meetings and club",
        },
        referrals: {
            name: "Invited",
            tagline: "Friends who came with you",
            lines: [
                { label: "A friend installed the app", amount: g(P.referral.install) },
                { label: "A friend started practising", amount: g(P.referral.friendStarted) },
                { label: "Someone new at a meeting", amount: g(P.referral.firstMeeting) },
                { label: "A friend bought through your link", amount: g(HOW_TO_EXAMPLES.friendCoursePart), note: "for a course part" },
            ],
            more: [
                `You get ${REFERRAL_BALANCE_PERCENT}% of your friends' purchases on your balance — spend it on new products.`,
                {
                    title: "A friend bought through your link:",
                    rows: [
                        { label: "Course part", amount: g(HOW_TO_EXAMPLES.friendCoursePart) },
                        { label: "MY REVIVE app, one year", amount: g(HOW_TO_EXAMPLES.friendAppYear) },
                        { label: "“Systematic Approach” package", amount: g(HOW_TO_EXAMPLES.friendBundle) },
                    ],
                },
                "A friend claimed your gift — points as for their purchase; a refund takes them back.",
            ],
            go: "Invite a friend",
        },
        learning: {
            name: "Learning",
            tagline: "Courses, lectures and the smart search",
            lines: [
                { label: "Course lesson", amount: g(P.learning.lessonMinute), note: "per minute" },
                { label: "Chapter mini-test", amount: g(MINI_TEST_POINTS) },
                { label: "Question on a lesson", amount: g(P.learning.lessonQuestion) },
                { label: "Question to the smart search", amount: g(SUP_QUESTION_POINTS) },
                { label: "Lecture or clip", amount: g(P.learning.freeMinute), note: "per minute" },
            ],
            more: [
                {
                    title: "Also:",
                    rows: [
                        { label: "A guide or self-diagnostics in the app", amount: g(P.learning.appLearning) },
                        { label: "A request to a specialist", amount: g(P.learning.specialistTicket) },
                    ],
                },
            ],
            go: "My courses",
        },
    },
};

const DE: HowToTexts = {
    title: "So sammeln Sie Punkte",
    intro: "Alles, was Sie tun, füllt eine von fünf Sphären. Hier sehen Sie, wofür es wie viel gibt.",
    of: (s) => `von ${s}`,
    fullAt: (s) => `Volle Sphäre — ${s}`,
    unitWeeks: (n) => plural(n, "de", { one: "Woche nach Plan", other: "Wochen nach Plan" }),
    unitPoints: (n) => plural(n, "de", { one: "Punkt", other: "Punkte" }),
    more: "Mehr",
    moreTitle: (name) => `${name}: mehr`,
    close: "Verstanden",
    spheres: {
        contrib: {
            name: "Beitrag",
            tagline: "Käufe, Bewertungen und Hilfe für das Projekt",
            lines: [
                { label: "Kursteil", amount: g(HOW_TO_EXAMPLES.coursePart) },
                { label: "App MY REVIVE, ein Jahr", amount: g(HOW_TO_EXAMPLES.appYear) },
                { label: "Paket „Systematischer Ansatz“", amount: g(HOW_TO_EXAMPLES.bundle) },
                { label: "Produktbewertung", amount: g(REVIEW_POINTS) },
                { label: "Untertitel-Korrektur eines Vortrags", amount: g(CORRECTION_POINTS_FOREIGN) },
                { label: "Ein Fehler auf der Website oder im Rezept", amount: g(ERROR_REPORT_POINTS) },
                { label: "Testtraining", amount: g(P.workouts.testWorkout) },
            ],
            more: [
                "Einen Kurs verschenkt — die Punkte gehören Ihnen, wie bei einem eigenen Kauf.",
                "Eine Rückerstattung nimmt die Punkte des Kaufs zurück.",
                {
                    title: "Außerdem:",
                    rows: [
                        { label: "10 Tokens für die intelligente Suche", amount: g(HOW_TO_EXAMPLES.tokenPack) },
                        { label: "Eine Diagnostik", amount: g(HOW_TO_EXAMPLES.diagnostic) },
                    ],
                },
            ],
            go: "Alle Produkte",
        },
        workouts: {
            name: "Übungen",
            tagline: "Training nach Plan in der App MY REVIVE",
            lines: [
                { label: "Eine Woche genau nach Plan", amount: g(WEEK_ON_PLAN_POINTS) },
                { label: "Ihr erster Plan", amount: g(P.workouts.planCreate), note: "einmalig" },
                { label: "Neuling mit vollem Plan", amount: g(P.workouts.newbieBonus), note: "zusätzlich für jede volle Woche" },
            ],
            more: [
                "Punkte gibt es für Training nach Plan: je näher die Woche am Plan, desto mehr Punkte.",
                "Ein neuer Plan bringt keine Punkte.",
            ],
            go: "Zum Training",
        },
        social: {
            name: "Gemeinschaft",
            tagline: "Treffen, der Club und gute Taten",
            lines: [
                { label: "Dienst-Treffen", amount: g(P.meeting.seva) },
                { label: "Clubtreffen", amount: g(P.meeting.club) },
                { label: "Organisator eines Treffens", amount: g(P.meeting.perParticipant), note: "für jeden Teilnehmer" },
                { label: "Einem Club beitreten", amount: g(P.club.joinClub), note: "einmalig" },
                { label: "Gute Tat", amount: g(P.club.goodDeed) },
            ],
            more: [
                {
                    title: "Zu einem Treffen gekommen:",
                    rows: [
                        { label: "Dienst", amount: g(P.meeting.seva) },
                        { label: "Gesundheit, Sport", amount: g(P.meeting.health) },
                        { label: "Spirituell", amount: g(P.meeting.spirit) },
                        { label: "Lernen, Kultur", amount: g(P.meeting.edu) },
                        { label: "Club, Gespräch", amount: g(P.meeting.club) },
                        { label: "Online", amount: g(P.meeting.online) },
                    ],
                },
                {
                    title: "Außerdem:",
                    rows: [
                        { label: "Einen Freund hinzufügen", amount: g(P.club.addFriend) },
                        { label: "Erste Nachricht im Chat", amount: g(P.club.firstMessage) },
                    ],
                },
            ],
            go: "Treffen und Club",
        },
        referrals: {
            name: "Eingeladene",
            tagline: "Freunde, die mit Ihnen gekommen sind",
            lines: [
                { label: "Ein Freund hat die App installiert", amount: g(P.referral.install) },
                { label: "Ein Freund hat mit dem Üben begonnen", amount: g(P.referral.friendStarted) },
                { label: "Jemand Neues beim Treffen", amount: g(P.referral.firstMeeting) },
                { label: "Ein Freund hat über Ihren Link gekauft", amount: g(HOW_TO_EXAMPLES.friendCoursePart), note: "für einen Kursteil" },
            ],
            more: [
                `Sie erhalten ${REFERRAL_BALANCE_PERCENT} % der Käufe Ihrer Freunde als Guthaben — einlösbar für neue Produkte.`,
                {
                    title: "Ein Freund hat über Ihren Link gekauft:",
                    rows: [
                        { label: "Kursteil", amount: g(HOW_TO_EXAMPLES.friendCoursePart) },
                        { label: "App MY REVIVE, ein Jahr", amount: g(HOW_TO_EXAMPLES.friendAppYear) },
                        { label: "Paket „Systematischer Ansatz“", amount: g(HOW_TO_EXAMPLES.friendBundle) },
                    ],
                },
                "Ein Freund hat Ihr Geschenk eingelöst — Punkte wie für seinen Kauf; eine Rückerstattung nimmt sie zurück.",
            ],
            go: "Einen Freund einladen",
        },
        learning: {
            name: "Lernen",
            tagline: "Kurse, Vorträge und die intelligente Suche",
            lines: [
                { label: "Kurslektion", amount: g(P.learning.lessonMinute), note: "pro Minute" },
                { label: "Mini-Test zum Kapitel", amount: g(MINI_TEST_POINTS) },
                { label: "Frage zu einer Lektion", amount: g(P.learning.lessonQuestion) },
                { label: "Frage an die intelligente Suche", amount: g(SUP_QUESTION_POINTS) },
                { label: "Vortrag oder Clip", amount: g(P.learning.freeMinute), note: "pro Minute" },
            ],
            more: [
                {
                    title: "Außerdem:",
                    rows: [
                        { label: "Ein Leitfaden oder Selbstdiagnose in der App", amount: g(P.learning.appLearning) },
                        { label: "Eine Anfrage an einen Spezialisten", amount: g(P.learning.specialistTicket) },
                    ],
                },
            ],
            go: "Meine Kurse",
        },
    },
};

const ES: HowToTexts = {
    title: "Cómo ganar puntos",
    intro: "Todo lo que haces llena una de cinco esferas. Aquí ves qué da cuánto.",
    of: (s) => `de ${s}`,
    fullAt: (s) => `Esfera llena: ${s}`,
    unitWeeks: (n) => plural(n, "es", { one: "semana según el plan", other: "semanas según el plan" }),
    unitPoints: (n) => plural(n, "es", { one: "punto", other: "puntos" }),
    more: "Más",
    moreTitle: (name) => `${name}: más`,
    close: "Entendido",
    spheres: {
        contrib: {
            name: "Aporte",
            tagline: "Compras, reseñas y ayuda al proyecto",
            lines: [
                { label: "Parte de un curso", amount: g(HOW_TO_EXAMPLES.coursePart) },
                { label: "App MY REVIVE, un año", amount: g(HOW_TO_EXAMPLES.appYear) },
                { label: "Paquete «Enfoque sistémico»", amount: g(HOW_TO_EXAMPLES.bundle) },
                { label: "Reseña de un producto", amount: g(REVIEW_POINTS) },
                { label: "Corrección de subtítulos de una conferencia", amount: g(CORRECTION_POINTS_FOREIGN) },
                { label: "Un error en el sitio o en una receta", amount: g(ERROR_REPORT_POINTS) },
                { label: "Entrenamiento de prueba", amount: g(P.workouts.testWorkout) },
            ],
            more: [
                "Regalaste un curso: los puntos son tuyos, como en tu propia compra.",
                "Un reembolso retira los puntos de la compra.",
                {
                    title: "Además:",
                    rows: [
                        { label: "10 tokens para la búsqueda inteligente", amount: g(HOW_TO_EXAMPLES.tokenPack) },
                        { label: "Un diagnóstico", amount: g(HOW_TO_EXAMPLES.diagnostic) },
                    ],
                },
            ],
            go: "Todos los productos",
        },
        workouts: {
            name: "Prácticas",
            tagline: "Entrenamientos según tu plan en la app MY REVIVE",
            lines: [
                { label: "Una semana exacta según el plan", amount: g(WEEK_ON_PLAN_POINTS) },
                { label: "Tu primer plan", amount: g(P.workouts.planCreate), note: "una vez" },
                { label: "Principiante con el plan completo", amount: g(P.workouts.newbieBonus), note: "extra por cada semana completa" },
            ],
            more: [
                "Los puntos vienen de entrenar según el plan: cuanto más cerca del plan esté la semana, más puntos.",
                "Hacer un plan nuevo no suma puntos.",
            ],
            go: "A los entrenamientos",
        },
        social: {
            name: "Comunidad",
            tagline: "Encuentros, el club y buenas acciones",
            lines: [
                { label: "Encuentro de servicio", amount: g(P.meeting.seva) },
                { label: "Encuentro del club", amount: g(P.meeting.club) },
                { label: "Organizar un encuentro", amount: g(P.meeting.perParticipant), note: "por cada participante" },
                { label: "Unirte a un club", amount: g(P.club.joinClub), note: "una vez" },
                { label: "Buena acción", amount: g(P.club.goodDeed) },
            ],
            more: [
                {
                    title: "Fuiste a un encuentro:",
                    rows: [
                        { label: "Servicio", amount: g(P.meeting.seva) },
                        { label: "Salud, deporte", amount: g(P.meeting.health) },
                        { label: "Espiritual", amount: g(P.meeting.spirit) },
                        { label: "Aprendizaje, cultura", amount: g(P.meeting.edu) },
                        { label: "Club, charla", amount: g(P.meeting.club) },
                        { label: "En línea", amount: g(P.meeting.online) },
                    ],
                },
                {
                    title: "Además:",
                    rows: [
                        { label: "Añadir un amigo", amount: g(P.club.addFriend) },
                        { label: "Primer mensaje en un chat", amount: g(P.club.firstMessage) },
                    ],
                },
            ],
            go: "Encuentros y club",
        },
        referrals: {
            name: "Invitados",
            tagline: "Amigos que llegaron contigo",
            lines: [
                { label: "Un amigo instaló la app", amount: g(P.referral.install) },
                { label: "Un amigo empezó a practicar", amount: g(P.referral.friendStarted) },
                { label: "Alguien nuevo en un encuentro", amount: g(P.referral.firstMeeting) },
                { label: "Un amigo compró con tu enlace", amount: g(HOW_TO_EXAMPLES.friendCoursePart), note: "por una parte de curso" },
            ],
            more: [
                `Recibes el ${REFERRAL_BALANCE_PERCENT} % de las compras de tus amigos en tu saldo: úsalo en nuevos productos.`,
                {
                    title: "Un amigo compró con tu enlace:",
                    rows: [
                        { label: "Parte de un curso", amount: g(HOW_TO_EXAMPLES.friendCoursePart) },
                        { label: "App MY REVIVE, un año", amount: g(HOW_TO_EXAMPLES.friendAppYear) },
                        { label: "Paquete «Enfoque sistémico»", amount: g(HOW_TO_EXAMPLES.friendBundle) },
                    ],
                },
                "Un amigo recibió tu regalo: puntos como por su compra; un reembolso los retira.",
            ],
            go: "Invitar a un amigo",
        },
        learning: {
            name: "Aprendizaje",
            tagline: "Cursos, conferencias y la búsqueda inteligente",
            lines: [
                { label: "Lección del curso", amount: g(P.learning.lessonMinute), note: "por minuto" },
                { label: "Minitest del capítulo", amount: g(MINI_TEST_POINTS) },
                { label: "Pregunta sobre una lección", amount: g(P.learning.lessonQuestion) },
                { label: "Pregunta a la búsqueda inteligente", amount: g(SUP_QUESTION_POINTS) },
                { label: "Conferencia o clip", amount: g(P.learning.freeMinute), note: "por minuto" },
            ],
            more: [
                {
                    title: "Además:",
                    rows: [
                        { label: "Una guía o autodiagnóstico en la app", amount: g(P.learning.appLearning) },
                        { label: "Una consulta a un especialista", amount: g(P.learning.specialistTicket) },
                    ],
                },
            ],
            go: "Mis cursos",
        },
    },
};

const CN: HowToTexts = {
    title: "如何获得积分",
    intro: "您做的每件事都会充满五个球中的一个。这里列出做什么、得多少。",
    of: (s) => `共 ${s}`,
    fullAt: (s) => `满球 — ${s}`,
    unitWeeks: () => "周（按计划）",
    unitPoints: () => "分",
    more: "详情",
    moreTitle: (name) => `${name}：详情`,
    close: "知道了",
    spheres: {
        contrib: {
            name: "贡献",
            tagline: "购买、评价和帮助项目",
            lines: [
                { label: "课程的一部分", amount: g(HOW_TO_EXAMPLES.coursePart) },
                { label: "MY REVIVE 应用一年", amount: g(HOW_TO_EXAMPLES.appYear) },
                { label: "「系统方法」套餐", amount: g(HOW_TO_EXAMPLES.bundle) },
                { label: "产品评价", amount: g(REVIEW_POINTS) },
                { label: "讲座字幕修正", amount: g(CORRECTION_POINTS_FOREIGN) },
                { label: "网站或食谱中的错误", amount: g(ERROR_REPORT_POINTS) },
                { label: "测试训练", amount: g(P.workouts.testWorkout) },
            ],
            more: [
                "赠送课程——积分归您，与自己购买相同。",
                "退款后，该购买的积分将被扣回。",
                {
                    title: "另外：",
                    rows: [
                        { label: "智能搜索 10 个代币", amount: g(HOW_TO_EXAMPLES.tokenPack) },
                        { label: "诊断", amount: g(HOW_TO_EXAMPLES.diagnostic) },
                    ],
                },
            ],
            go: "所有产品",
        },
        workouts: {
            name: "练习",
            tagline: "在 MY REVIVE 应用中按计划训练",
            lines: [
                { label: "严格按计划完成一周", amount: g(WEEK_ON_PLAN_POINTS) },
                { label: "第一个计划", amount: g(P.workouts.planCreate), note: "仅一次" },
                { label: "新手完成完整计划", amount: g(P.workouts.newbieBonus), note: "每完成一整周额外获得" },
            ],
            more: [
                "积分来自按计划训练：一周越接近计划，积分越多。",
                "重新制定计划不会增加积分。",
            ],
            go: "前往训练",
        },
        social: {
            name: "社区",
            tagline: "聚会、俱乐部和善行",
            lines: [
                { label: "服务活动", amount: g(P.meeting.seva) },
                { label: "俱乐部聚会", amount: g(P.meeting.club) },
                { label: "聚会组织者", amount: g(P.meeting.perParticipant), note: "每位参与者" },
                { label: "加入俱乐部", amount: g(P.club.joinClub), note: "仅一次" },
                { label: "善行", amount: g(P.club.goodDeed) },
            ],
            more: [
                {
                    title: "参加聚会：",
                    rows: [
                        { label: "服务", amount: g(P.meeting.seva) },
                        { label: "健康、运动", amount: g(P.meeting.health) },
                        { label: "灵性", amount: g(P.meeting.spirit) },
                        { label: "学习、文化", amount: g(P.meeting.edu) },
                        { label: "俱乐部、交流", amount: g(P.meeting.club) },
                        { label: "线上", amount: g(P.meeting.online) },
                    ],
                },
                {
                    title: "另外：",
                    rows: [
                        { label: "添加好友", amount: g(P.club.addFriend) },
                        { label: "聊天中的第一条消息", amount: g(P.club.firstMessage) },
                    ],
                },
            ],
            go: "聚会和俱乐部",
        },
        referrals: {
            name: "邀请",
            tagline: "和您一起来的朋友",
            lines: [
                { label: "朋友安装了应用", amount: g(P.referral.install) },
                { label: "朋友开始练习", amount: g(P.referral.friendStarted) },
                { label: "聚会来了新人", amount: g(P.referral.firstMeeting) },
                { label: "朋友通过您的链接购买", amount: g(HOW_TO_EXAMPLES.friendCoursePart), note: "课程的一部分" },
            ],
            more: [
                `朋友购买额的 ${REFERRAL_BALANCE_PERCENT}% 计入您的余额：可用于购买新产品。`,
                {
                    title: "朋友通过您的链接购买：",
                    rows: [
                        { label: "课程的一部分", amount: g(HOW_TO_EXAMPLES.friendCoursePart) },
                        { label: "MY REVIVE 应用一年", amount: g(HOW_TO_EXAMPLES.friendAppYear) },
                        { label: "「系统方法」套餐", amount: g(HOW_TO_EXAMPLES.friendBundle) },
                    ],
                },
                "朋友领取了您的礼物——积分与其购买相同；退款后积分将被扣回。",
            ],
            go: "邀请朋友",
        },
        learning: {
            name: "学习",
            tagline: "课程、讲座和智能搜索",
            lines: [
                { label: "课程课时", amount: g(P.learning.lessonMinute), note: "每分钟" },
                { label: "章节小测验", amount: g(MINI_TEST_POINTS) },
                { label: "课程提问", amount: g(P.learning.lessonQuestion) },
                { label: "向智能搜索提问", amount: g(SUP_QUESTION_POINTS) },
                { label: "讲座或片段", amount: g(P.learning.freeMinute), note: "每分钟" },
            ],
            more: [
                {
                    title: "另外：",
                    rows: [
                        { label: "应用中的指南或自我诊断", amount: g(P.learning.appLearning) },
                        { label: "向专家咨询", amount: g(P.learning.specialistTicket) },
                    ],
                },
            ],
            go: "我的课程",
        },
    },
};

const AR: HowToTexts = {
    title: "كيف تكسب النقاط",
    intro: "كل ما تفعله يملأ واحدة من خمس كرات. إليك ما يمنح النقاط وكم.",
    of: (s) => `من ${s}`,
    fullAt: (s) => `الكرة الممتلئة — ${s}`,
    unitWeeks: (n) =>
        arPlural(Math.abs(Math.round(n)), { few: "أسابيع وفق الخطة", many: "أسبوعًا وفق الخطة", other: "أسبوع وفق الخطة" }),
    unitPoints: (n) => arPlural(Math.abs(Math.round(n)), { few: "نقاط", other: "نقطة" }),
    more: "المزيد",
    moreTitle: (name) => `${name}: المزيد`,
    close: "فهمت",
    spheres: {
        contrib: {
            name: "المساهمة",
            tagline: "المشتريات والمراجعات ومساعدة المشروع",
            lines: [
                { label: "جزء من دورة", amount: g(HOW_TO_EXAMPLES.coursePart) },
                { label: "سنة من تطبيق MY REVIVE", amount: g(HOW_TO_EXAMPLES.appYear) },
                { label: "باقة «النهج المنظومي»", amount: g(HOW_TO_EXAMPLES.bundle) },
                { label: "مراجعة منتج", amount: g(REVIEW_POINTS) },
                { label: "تصحيح الترجمة النصية لمحاضرة", amount: g(CORRECTION_POINTS_FOREIGN) },
                { label: "خطأ في الموقع أو في وصفة", amount: g(ERROR_REPORT_POINTS) },
                { label: "تمرين تجريبي", amount: g(P.workouts.testWorkout) },
            ],
            more: [
                "أهديت دورة — النقاط لك، كما لو اشتريتها لنفسك.",
                "الاسترداد يسحب نقاط الشراء.",
                {
                    title: "وأيضًا:",
                    rows: [
                        { label: "10 رموز للبحث الذكي", amount: g(HOW_TO_EXAMPLES.tokenPack) },
                        { label: "تشخيص", amount: g(HOW_TO_EXAMPLES.diagnostic) },
                    ],
                },
            ],
            go: "كل المنتجات",
        },
        workouts: {
            name: "التمارين",
            tagline: "التمارين وفق خطتك في تطبيق MY REVIVE",
            lines: [
                { label: "أسبوع وفق الخطة تمامًا", amount: g(WEEK_ON_PLAN_POINTS) },
                { label: "خطتك الأولى", amount: g(P.workouts.planCreate), note: "مرة واحدة" },
                { label: "مبتدئ على الخطة الكاملة", amount: g(P.workouts.newbieBonus), note: "إضافة عن كل أسبوع كامل" },
            ],
            more: [
                "تأتي النقاط من التمارين وفق خطتك: كلما اقترب الأسبوع من الخطة زادت النقاط.",
                "وضع خطة جديدة لا يضيف نقاطًا.",
            ],
            go: "إلى التمارين",
        },
        social: {
            name: "المجتمع",
            tagline: "اللقاءات والنادي والأعمال الطيبة",
            lines: [
                { label: "لقاء خدمة (سيفا)", amount: g(P.meeting.seva) },
                { label: "لقاء النادي", amount: g(P.meeting.club) },
                { label: "منظِّم اللقاء", amount: g(P.meeting.perParticipant), note: "عن كل مشارك" },
                { label: "الانضمام إلى نادٍ", amount: g(P.club.joinClub), note: "مرة واحدة" },
                { label: "عمل طيب", amount: g(P.club.goodDeed) },
            ],
            more: [
                {
                    title: "حضرت لقاءً:",
                    rows: [
                        { label: "خدمة (سيفا)", amount: g(P.meeting.seva) },
                        { label: "صحة، رياضة", amount: g(P.meeting.health) },
                        { label: "روحي", amount: g(P.meeting.spirit) },
                        { label: "تعلّم، ثقافة", amount: g(P.meeting.edu) },
                        { label: "نادٍ، أحاديث", amount: g(P.meeting.club) },
                        { label: "عبر الإنترنت", amount: g(P.meeting.online) },
                    ],
                },
                {
                    title: "وأيضًا:",
                    rows: [
                        { label: "أضف صديقًا", amount: g(P.club.addFriend) },
                        { label: "الرسالة الأولى في محادثة", amount: g(P.club.firstMessage) },
                    ],
                },
            ],
            go: "اللقاءات والنادي",
        },
        referrals: {
            name: "المدعوّون",
            tagline: "أصدقاء جاؤوا معك",
            lines: [
                { label: "ثبّت صديق التطبيق", amount: g(P.referral.install) },
                { label: "بدأ صديق الممارسة", amount: g(P.referral.friendStarted) },
                { label: "شخص جديد في لقاء", amount: g(P.referral.firstMeeting) },
                { label: "اشترى صديق عبر رابطك", amount: g(HOW_TO_EXAMPLES.friendCoursePart), note: "عن جزء من دورة" },
            ],
            more: [
                `لك ${REFERRAL_BALANCE_PERCENT}% من مشتريات أصدقائك في رصيدك — يمكنك إنفاقها على منتجات جديدة.`,
                {
                    title: "اشترى صديق عبر رابطك:",
                    rows: [
                        { label: "جزء من دورة", amount: g(HOW_TO_EXAMPLES.friendCoursePart) },
                        { label: "سنة من تطبيق MY REVIVE", amount: g(HOW_TO_EXAMPLES.friendAppYear) },
                        { label: "باقة «النهج المنظومي»", amount: g(HOW_TO_EXAMPLES.friendBundle) },
                    ],
                },
                "استلم صديق هديتك — نقاط كما عن شرائه؛ والاسترداد يسحبها.",
            ],
            go: "ادعُ صديقًا",
        },
        learning: {
            name: "التعلّم",
            tagline: "الدورات والمحاضرات والبحث الذكي",
            lines: [
                { label: "درس من الدورة", amount: g(P.learning.lessonMinute), note: "للدقيقة" },
                { label: "اختبار قصير على الفصل", amount: g(MINI_TEST_POINTS) },
                { label: "سؤال عن درس", amount: g(P.learning.lessonQuestion) },
                { label: "سؤال إلى البحث الذكي", amount: g(SUP_QUESTION_POINTS) },
                { label: "محاضرة أو مقطع", amount: g(P.learning.freeMinute), note: "للدقيقة" },
            ],
            more: [
                {
                    title: "وأيضًا:",
                    rows: [
                        { label: "دليل أو تشخيص ذاتي في التطبيق", amount: g(P.learning.appLearning) },
                        { label: "طلب إلى مختص", amount: g(P.learning.specialistTicket) },
                    ],
                },
            ],
            go: "دوراتي",
        },
    },
};

const HOW_TO_BY_LANG: Record<string, HowToTexts> = { ru: RU, en: EN, de: DE, es: ES, cn: CN, zh: CN, ar: AR };

/**
 * Язык экрана: окно «за что эти баллы» ведёт сюда со страницы любого языка. Арабский —
 * только при включённом арабском интерфейсе (shared/config/arabicUi), иначе /ar — en, как
 * весь сайт там; незнакомый — en.
 */
const howToLang = (lang?: string): string => {
    const l = (lang || "").slice(0, 2).toLowerCase();
    return l === "ar" && !isArabicUi(l) ? "en" : l;
};

const byLang = (lang?: string): HowToTexts => HOW_TO_BY_LANG[howToLang(lang)] || EN;

/** Строка «А ещё» у человека на пакетах (.com): пакет на месяц вместо «10 токенов». */
const PLAN_PACK_LABEL: Record<string, string> = {
    ru: "Пакет поиска и вопросов на месяц",
    en: "A one-month search and questions pack",
    de: "Paket Suche und Fragen für einen Monat",
    es: "Paquete de búsqueda y preguntas de un mes",
    cn: "一个月搜索和提问套餐",
    zh: "一个月搜索和提问套餐",
    ar: "باقة البحث والأسئلة لشهر واحد",
};

/**
 * Тексты экрана. `plans` — человек на пакетах «поиск + вопросы» (.com): токенов у него нет,
 * строка «10 токенов для умного поиска» в «А ещё» — пакет на месяц и его баллы.
 */
export const howToTexts = (lang?: string, opts: { plans?: boolean } = {}): HowToTexts => {
    const tx = byLang(lang);
    if (!opts.plans) return tx;
    const label = PLAN_PACK_LABEL[howToLang(lang)] || PLAN_PACK_LABEL.en;
    const contrib = tx.spheres.contrib;
    const more = contrib.more.map((item, i) =>
        i === HOW_TO_TOKEN_ROW.more && typeof item === "object"
            ? {
                  ...item,
                  rows: item.rows.map((r, j) =>
                      j === HOW_TO_TOKEN_ROW.row ? { ...r, label, amount: g(HOW_TO_EXAMPLES.planPack) } : r,
                  ),
              }
            : item,
    );
    return { ...tx, spheres: { ...tx.spheres, contrib: { ...contrib, more } } };
};

/** Где в «Вкладе → Подробнее» строка пакета токенов: таблица «А ещё», первая строка. */
export const HOW_TO_TOKEN_ROW = { more: 2, row: 0 } as const;

/** Действия строк у человека на пакетах: строка пакета — свой ctx «plan» (его число баллов). */
export const howToRowActions = (plans = false): typeof HOW_TO_ROW_ACTIONS => {
    if (!plans) return HOW_TO_ROW_ACTIONS;
    const c = HOW_TO_ROW_ACTIONS.contrib;
    return {
        ...HOW_TO_ROW_ACTIONS,
        contrib: {
            ...c,
            more: c.more.map((rows, i) =>
                i === HOW_TO_TOKEN_ROW.more && rows
                    ? rows.map((r, j) => (j === HOW_TO_TOKEN_ROW.row ? { ...r, ctx: "plan" } : r))
                    : rows,
            ),
        },
    };
};
